import React, { useState, useEffect } from 'react';
import { apiService, Provider, ProviderRole } from '../../services/apiService';

const ROLE_OPTIONS: Array<{ value: ProviderRole; label: string }> = [
  { value: 'dentist', label: 'Zahnarzt' },
  { value: 'hygienist', label: 'Zahnprophylaxe / Hygiene' },
];

const roleLabel = (r: ProviderRole | undefined) =>
  ROLE_OPTIONS.find(o => o.value === r)?.label ?? r ?? '—';

const emptyProvider: Partial<Provider> = {
  name: '',
  email: '',
  phone: '',
  specialization: '',
  role: 'dentist',
  google_calendar_id: '',
  is_active: true,
};

const ProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Provider> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getProviders(false);
      setProviders(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name || !editing.email) {
      setError('Name and email are required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.id) {
        await apiService.updateProvider(editing.id, editing);
      } else {
        await apiService.createProvider(editing);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p: Provider) => {
    if (!window.confirm(`Delete provider "${p.name}"?`)) return;
    try {
      await apiService.deleteProvider(p.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Providers</h2>
        <button
          onClick={() => setEditing({ ...emptyProvider })}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-navy-admin hover:bg-blue-900"
        >
          + Add Provider
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded">{error}</div>}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : providers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No providers yet.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rolle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Specialization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GCal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {providers.map(p => (
                <tr key={p.id} className={p.is_active ? '' : 'opacity-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${p.role === 'hygienist' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {roleLabel(p.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{p.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{p.phone}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{p.specialization}</td>
                  <td className="px-4 py-3 text-sm">
                    {p.google_calendar_id
                      ? <span title={p.google_calendar_id} className="text-xs text-green-700">✓ verknüpft</span>
                      : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {p.is_active
                      ? <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                      : <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">Inactive</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm space-x-2">
                    <button onClick={() => setEditing({ ...p })} className="text-blue-600 hover:text-blue-900">Edit</button>
                    <button onClick={() => onDelete(p)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-bold mb-4">{editing.id ? 'Edit Provider' : 'New Provider'}</h3>
            <div className="space-y-3">
              <Field label="Name *" value={editing.name || ''} onChange={v => setEditing({ ...editing, name: v })} />
              <Field label="Email *" type="email" value={editing.email || ''} onChange={v => setEditing({ ...editing, email: v })} />
              <Field label="Phone" value={editing.phone || ''} onChange={v => setEditing({ ...editing, phone: v })} />
              <Field label="Specialization (Anzeige)" value={editing.specialization || ''} onChange={v => setEditing({ ...editing, specialization: v })} />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rolle *</label>
                <select
                  value={editing.role || 'dentist'}
                  onChange={e => setEditing({ ...editing, role: e.target.value as ProviderRole })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-navy-admin"
                >
                  {ROLE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Bestimmt, welche Behandlungen dieser Behandler abdecken kann (z. B. Zahnreinigung → Hygiene, Beratung/Behandlung → Zahnarzt).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Calendar ID</label>
                <input
                  type="text"
                  value={editing.google_calendar_id || ''}
                  onChange={e => setEditing({ ...editing, google_calendar_id: e.target.value })}
                  placeholder="z. B. dra.garcia@seadent.es"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-navy-admin font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Eigener Kalender dieses Behandlers. Service-Account muss Lesezugriff haben. Leer lassen, wenn Termine nur über das Admin gepflegt werden.
                </p>
              </div>

              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" checked={!!editing.is_active}
                  onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                <span>Active</span>
              </label>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button onClick={() => setEditing(null)} disabled={saving}
                className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={onSave} disabled={saving}
                className="px-4 py-2 bg-navy-admin text-white rounded hover:bg-blue-900 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string }> = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-navy-admin"
    />
  </div>
);

export default ProvidersPage;
